import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile, fileUrl , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Upload, X } from "lucide-react";

const empty = {
  nama: "", tempat_lahir: "", tanggal_lahir: "", jenis_kelamin: "L", niy: "",
  nuptk: "", nrg: "", email: "", telp: "", pendidikan_terakhir: "", tmt: "",
  alamat: "", foto_file_id: null, status: "aktif",
};

export default function AdminGuru() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState(""); const [jk, setJk] = useState(""); const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [applied, setApplied] = useState({ q: "", jk: "", status: "" });
  const load = useCallback(async () => {
    try {
      const params = {};
      if (applied.q) params.q = applied.q;
      if (applied.jk) params.jenis_kelamin = applied.jk;
      if (applied.status) params.status = applied.status;
      const { data } = await api.get("/guru", { params }); setItems(data);
      const { data: st } = await api.get("/guru/stats"); setStats(st);
    } catch (e) { console.error("Load guru failed:", e); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const apply = () => setApplied({ q, jk, status });
  const reset = () => { setQ(""); setJk(""); setStatus(""); setApplied({ q: "", jk: "", status: "" }); };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const res = await uploadFile(f); setForm({ ...form, foto_file_id: res.id }); toast.success("Foto diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/guru/${editId}`, form); else await api.post("/guru", form);
      toast.success(editId ? "Data diperbarui" : "Guru ditambahkan");
      setShowForm(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (g) => { setForm({ ...empty, ...g }); setEditId(g.id); setShowForm(true); };
  const del = async (id) => { if (!window.confirm("Hapus guru?")) return; await api.delete(`/guru/${id}`); toast.success("Dihapus"); load(); };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Kelola Data Guru" action={
        <button onClick={() => { setShowForm(true); setForm(empty); setEditId(null); }} className="pp-btn-primary" data-testid="open-create-guru">
          <Plus className="w-4 h-4" /> Input Guru Baru
        </button>
      } />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Guru Aktif", value: stats.aktif ?? 0 },
          { label: "Guru Tidak Aktif", value: stats.tidak_aktif ?? 0 },
          { label: "Perempuan Aktif", value: stats.perempuan_aktif ?? 0 },
          { label: "Laki-laki Aktif", value: stats.laki_aktif ?? 0 },
        ].map((s) => (
          <div key={s.label} className="pp-card p-4">
            <div className="text-xs text-slate-500">{s.label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{s.value}</div>
          </div>
        ))}
      </section>

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3">
          <input className="pp-input" placeholder="Cari nama guru" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="pp-input" value={jk} onChange={(e) => setJk(e.target.value)}>
            <option value="">Semua jenis kelamin</option><option value="L">Laki-laki</option><option value="P">Perempuan</option>
          </select>
          <select className="pp-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Semua status</option><option value="aktif">Aktif</option><option value="tidak_aktif">Tidak Aktif</option>
          </select>
          <div className="flex gap-2"><button onClick={apply} className="pp-btn-primary flex-1" data-testid="guru-filter-apply">Terapkan</button><button onClick={reset} className="pp-btn-secondary flex-1" data-testid="guru-filter-reset">Reset</button></div>
        </div>
      </section>

      {showForm && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold">{editId ? "Edit Guru" : "Input Guru Baru"}</h3>
            <button onClick={() => { setShowForm(false); setForm(empty); setEditId(null); }}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="guru-form">
            <div><label className="pp-label">Nama Guru *</label><input required className="pp-input" value={form.nama} onChange={(e) => setForm({...form, nama: e.target.value})} data-testid="g-nama" /></div>
            <div><label className="pp-label">NIY *</label><input required className="pp-input" value={form.niy} onChange={(e) => setForm({...form, niy: e.target.value})} data-testid="g-niy" /></div>
            <div><label className="pp-label">Tempat Lahir</label><input className="pp-input" value={form.tempat_lahir} onChange={(e) => setForm({...form, tempat_lahir: e.target.value})} /></div>
            <div><label className="pp-label">Tanggal Lahir</label><input type="date" className="pp-input" value={form.tanggal_lahir || ""} onChange={(e) => setForm({...form, tanggal_lahir: e.target.value})} /></div>
            <div><label className="pp-label">Jenis Kelamin</label>
              <select className="pp-input" value={form.jenis_kelamin} onChange={(e) => setForm({...form, jenis_kelamin: e.target.value})}>
                <option value="L">Laki-laki</option><option value="P">Perempuan</option>
              </select></div>
            <div><label className="pp-label">Status</label>
              <select className="pp-input" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                <option value="aktif">Aktif</option><option value="tidak_aktif">Tidak Aktif</option>
              </select></div>
            <div><label className="pp-label">NUPTK</label><input className="pp-input" value={form.nuptk} onChange={(e) => setForm({...form, nuptk: e.target.value})} /></div>
            <div><label className="pp-label">NRG (Nomor Registrasi Guru)</label><input className="pp-input" value={form.nrg} onChange={(e) => setForm({...form, nrg: e.target.value})} /></div>
            <div><label className="pp-label">Email</label><input type="email" className="pp-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><label className="pp-label">No. Telp</label><input className="pp-input" value={form.telp} onChange={(e) => setForm({...form, telp: e.target.value})} /></div>
            <div><label className="pp-label">Pendidikan Terakhir</label><input className="pp-input" value={form.pendidikan_terakhir} onChange={(e) => setForm({...form, pendidikan_terakhir: e.target.value})} /></div>
            <div><label className="pp-label">TMT (Tgl Masa Tugas)</label><input type="date" className="pp-input" value={form.tmt || ""} onChange={(e) => setForm({...form, tmt: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Alamat</label><textarea rows={2} className="pp-input" value={form.alamat} onChange={(e) => setForm({...form, alamat: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Foto Guru</label>
              <label className="pp-btn-secondary cursor-pointer">
                <Upload className="w-4 h-4" /> {uploading ? "Mengunggah…" : (form.foto_file_id ? "Ganti Foto" : "Pilih Foto")}
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              </label>
              {form.foto_file_id && <span className="ml-3 text-sm text-emerald-600">✓ Foto siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2 pt-4 border-t border-slate-200">
              <button type="button" onClick={() => { setShowForm(false); setForm(empty); setEditId(null); }} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-guru">{editId ? "Simpan Perubahan" : "Simpan Data Guru"}</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="guru-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Nama</th>
              <th className="text-left px-5 py-3">NIY</th>
              <th className="text-left px-5 py-3">JK</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Belum ada data guru.</td></tr>}
              {items.map((g) => (
                <tr key={g.id} className="pp-row" data-testid={`guru-row-${g.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{g.nama}</td>
                  <td className="px-5 py-3 font-mono text-xs">{g.niy}</td>
                  <td className="px-5 py-3">{g.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"}</td>
                  <td className="px-5 py-3"><span className={g.status === "aktif" ? "pp-badge-success" : "pp-badge-danger"}>{g.status === "aktif" ? "Aktif" : "Tidak Aktif"}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setDetail(g)} className="pp-btn-ghost text-xs"><Eye className="w-3.5 h-3.5" />Detail</button>
                      <button onClick={() => startEdit(g)} className="pp-btn-ghost text-xs"><Pencil className="w-3.5 h-3.5" />Edit</button>
                      <button onClick={() => del(g.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">Detail Guru</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="flex gap-5 mb-5">
              {detail.foto_file_id ? <img src={fileUrl(detail.foto_file_id)} className="w-28 h-28 rounded-xl object-cover border border-slate-200" alt="Foto" />
                : <div className="w-28 h-28 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300 text-4xl font-bold">{detail.nama[0]}</div>}
              <div>
                <div className="text-2xl font-bold">{detail.nama}</div>
                <div className="font-mono text-sm text-slate-500">NIY: {detail.niy}</div>
                <div className="mt-2"><span className={detail.status === "aktif" ? "pp-badge-success" : "pp-badge-danger"}>{detail.status}</span></div>
              </div>
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Tempat/Tgl Lahir", `${detail.tempat_lahir || "—"}, ${formatTanggal(detail.tanggal_lahir)}`],
                ["Jenis Kelamin", detail.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"],
                ["NUPTK", detail.nuptk || "—"], ["NRG", detail.nrg || "—"],
                ["Email", detail.email || "—"], ["No. Telp", detail.telp || "—"],
                ["Pendidikan Terakhir", detail.pendidikan_terakhir || "—"], ["TMT", detail.tmt || "—"],
                ["Alamat", detail.alamat || "—"],
              ].map(([k, v]) => (
                <div key={k}><dt className="text-xs uppercase text-slate-500">{k}</dt><dd className="text-slate-900 mt-0.5">{v}</dd></div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </Layout>
  );
}
